<template>
  <div class="chat-widget" :class="{ 'chat-widget--open': isOpen }">
    <!-- Floating launcher -->
    <button v-show="!isOpen"
            class="chat-widget__fab"
            :aria-label="$t('chat.openLabel')"
            @click="open">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    </button>

    <!-- Chat panel -->
    <div v-show="isOpen" class="chat-widget__panel">
      <header class="chat-widget__header">
        <div class="chat-widget__title">
          <span class="chat-widget__dot"></span>
          {{ $t('chat.title') }}
        </div>
        <button class="chat-widget__close" :aria-label="$t('chat.closeLabel')" @click="close">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </header>

      <div class="chat-widget__body" ref="body">
        <div class="chat-msg chat-msg--assistant">
          <div class="chat-msg__bubble">{{ $t('chat.greeting') }}</div>
        </div>
        <div v-for="(m, i) in messages" :key="i"
             class="chat-msg" :class="'chat-msg--' + m.role">
          <div v-if="m.role === 'assistant'"
               class="chat-msg__bubble chat-msg__bubble--md"
               v-html="renderMarkdown(m.content)"></div>
          <div v-else class="chat-msg__bubble" v-text="m.content"></div>
        </div>
        <div v-if="sending" class="chat-msg chat-msg--assistant">
          <div class="chat-msg__bubble chat-msg__bubble--typing">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div v-if="askEmail && !emailSent" class="chat-email-prompt">
          <input v-model="emailInput"
                 type="email"
                 :placeholder="$t('chat.emailPlaceholder')"
                 @keyup.enter="submitEmail"/>
          <button @click="submitEmail" :disabled="!validEmail || submittingEmail">
            {{ $t('chat.emailSubmit') }}
          </button>
        </div>
        <div v-if="emailSent" class="chat-msg chat-msg--system">
          <div class="chat-msg__bubble chat-msg__bubble--system">{{ $t('chat.emailDone') }}</div>
        </div>
      </div>

      <footer class="chat-widget__footer">
        <textarea v-model="input"
                  rows="1"
                  :placeholder="$t('chat.placeholder')"
                  :disabled="sending"
                  @keydown.enter.exact.prevent="send"></textarea>
        <button class="chat-widget__send"
                @click="send"
                :disabled="!input.trim() || sending"
                :aria-label="$t('chat.send')">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import api from 'src/api'
import { mapGetters } from 'vuex'
import { marked } from 'marked'

const STORAGE_KEY = 'ipastbook:chat:sessionId'

marked.setOptions({
  breaks: true,
  gfm: true,
  mangle: false,
  headerIds: false
})

// 极简 sanitize：只允许我们用得到的标签，剥离 script/style/iframe/event 等。
const ALLOWED_TAGS = /^<\/?(p|br|hr|strong|b|em|i|u|s|del|code|pre|blockquote|ul|ol|li|table|thead|tbody|tr|th|td|h[1-6]|a|span|img)\b[^>]*\/?>$/i
// 图片 src 只允许我们的官方域名（主站 + CDN）
const IMG_SRC_WHITELIST = /^https:\/\/((static|www)\.)?(ipastbook|weixinshu|xinshu)\.(com|me)\//i
function sanitizeHtml (html) {
  return html.replace(/<[^>]+>/g, (tag) => {
    if (!ALLOWED_TAGS.test(tag)) return ''
    // 剥事件 + javascript:
    const clean = tag
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '')
      .replace(/(href|src)\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi, '$1="#"')
    // <img> src 必须在白名单
    if (/^<img\b/i.test(clean)) {
      const m = clean.match(/src\s*=\s*"([^"]+)"/i) || clean.match(/src\s*=\s*'([^']+)'/i)
      if (!m || !IMG_SRC_WHITELIST.test(m[1])) return ''
    }
    return clean
  })
}

export default {
  name: 'chat-widget',
  data () {
    return {
      isOpen: false,
      input: '',
      messages: [],
      sending: false,
      sessionId: '',
      askEmail: false,
      emailInput: '',
      emailSent: false,
      submittingEmail: false,
      historyLoaded: false
    }
  },
  computed: {
    ...mapGetters(['language', 'user']),
    validEmail () {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.emailInput.trim())
    }
  },
  watch: {
    'user.uid' (val) {
      if (val) this.loadLatestSession()
    }
  },
  mounted () {
    try {
      this.sessionId = localStorage.getItem(STORAGE_KEY) || ''
    } catch (e) { /* localStorage unavailable */ }
    if (this.user && this.user.uid) {
      this.loadLatestSession()
    } else if (this.sessionId) {
      this.loadHistory(this.sessionId)
    }
  },
  methods: {
    open () {
      this.isOpen = true
      this.$nextTick(() => this.scrollToBottom())
    },
    close () {
      this.isOpen = false
    },
    persistSession (id) {
      this.sessionId = id
      try { localStorage.setItem(STORAGE_KEY, id) } catch (e) {}
    },
    scrollToBottom () {
      const el = this.$refs.body
      if (el) el.scrollTop = el.scrollHeight
    },
    async loadLatestSession () {
      try {
        const res = await api.chatSessions()
        if (res && res.ok && res.sessions && res.sessions.length) {
          const latest = res.sessions[0].sessionId
          await this.loadHistory(latest)
          this.persistSession(latest)
        } else if (this.sessionId) {
          await this.loadHistory(this.sessionId)
        }
      } catch (e) { /* ignore */ }
    },
    async loadHistory (sid) {
      if (!sid || this.historyLoaded) return
      try {
        const res = await api.chatHistory(sid)
        if (res && res.ok) {
          this.messages = (res.messages || []).map(m => ({
            role: m.role, content: m.content
          }))
          this.emailSent = !!res.userEmail
          this.historyLoaded = true
          this.$nextTick(() => this.scrollToBottom())
        }
      } catch (e) { /* ignore */ }
    },
    async send () {
      const text = this.input.trim()
      if (!text || this.sending) return
      this.messages.push({ role: 'user', content: text })
      this.input = ''
      this.sending = true
      this.$nextTick(() => this.scrollToBottom())
      try {
        const res = await api.chatSendMessage({
          message: text,
          sessionId: this.sessionId,
          language: this.language
        })
        if (res && res.ok) {
          this.persistSession(res.sessionId)
          this.messages.push({ role: 'assistant', content: res.reply })
          this.askEmail = !!res.askEmail
          if (res.escalated) this.emailSent = true
        } else {
          this.messages.push({ role: 'assistant', content: this.$t('chat.errorRetry') })
        }
      } catch (e) {
        this.messages.push({ role: 'assistant', content: this.$t('chat.errorRetry') })
      }
      this.sending = false
      this.$nextTick(() => this.scrollToBottom())
    },
    renderMarkdown (text) {
      if (!text) return ''
      try {
        return sanitizeHtml(marked.parse(text))
      } catch (e) {
        return (text || '').replace(/[<>&]/g, c => ({
          '<': '&lt;', '>': '&gt;', '&': '&amp;'
        }[c]))
      }
    },
    async submitEmail () {
      if (!this.validEmail || this.submittingEmail) return
      this.submittingEmail = true
      try {
        const res = await api.chatEscalate({
          sessionId: this.sessionId,
          email: this.emailInput.trim()
        })
        if (res && res.ok) {
          this.emailSent = true
          this.askEmail = false
        }
      } catch (e) { /* ignore */ }
      this.submittingEmail = false
      this.$nextTick(() => this.scrollToBottom())
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';

.chat-widget {
  position: fixed;
  z-index: 1500;
  bottom: 20px;
  right: 20px;
  font-size: 14px;
  color: #1a1a2e;

  &__fab {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: $el-color-primary;
    color: #fff;
    border: none;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(245, 99, 104, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s;

    &:hover { transform: scale(1.06); }
  }

  &__panel {
    width: 360px;
    height: 540px;
    max-height: calc(100vh - 40px);
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 600px) {
      width: calc(100vw - 24px);
      height: calc(100vh - 100px);
    }
  }

  &__header {
    padding: 14px 16px;
    background: linear-gradient(135deg, $el-color-primary, #ff878b);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4ade80;
    box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.3);
  }

  &__close {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 4px;
    opacity: 0.85;

    &:hover { opacity: 1; }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #fafafa;
    display: flex;
    flex-direction: column;
    gap: 10px;
    -webkit-overflow-scrolling: touch;
  }

  &__footer {
    padding: 10px 12px;
    border-top: 1px solid #f0f0f5;
    display: flex;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;

    textarea {
      flex: 1;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 8px 12px;
      font-size: 14px;
      font-family: inherit;
      resize: none;
      outline: none;
      max-height: 96px;
      line-height: 1.4;

      &:focus { border-color: $el-color-primary; }
    }
  }

  &__send {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: $el-color-primary;
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:disabled {
      background: #d4d4d4;
      cursor: not-allowed;
    }
  }
}

.chat-msg {
  display: flex;

  &--user { justify-content: flex-end; }
  &--assistant, &--system { justify-content: flex-start; }

  &__bubble {
    max-width: 78%;
    padding: 9px 13px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #f0f0f5;
    line-height: 1.5;
    word-wrap: break-word;
    white-space: pre-wrap;

    &--typing {
      display: inline-flex;
      gap: 4px;
      padding: 12px 14px;

      span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #c4c4c4;
        animation: chat-typing 1.2s infinite;

        &:nth-child(2) { animation-delay: 0.15s; }
        &:nth-child(3) { animation-delay: 0.3s; }
      }
    }

    &--system {
      background: rgba(74, 222, 128, 0.12);
      border-color: rgba(74, 222, 128, 0.3);
      color: #15803d;
      font-size: 13px;
    }
  }

  &--user &__bubble {
    background: $el-color-primary;
    color: #fff;
    border-color: $el-color-primary;
  }

  &__bubble--md {
    p { margin: 0 0 8px; &:last-child { margin-bottom: 0; } }
    h1, h2, h3, h4, h5, h6 {
      margin: 10px 0 6px; font-weight: 600; line-height: 1.3;
    }
    h1 { font-size: 16px; }
    h2 { font-size: 15px; }
    h3, h4, h5, h6 { font-size: 14px; }
    ul, ol { margin: 4px 0 8px; padding-left: 22px; }
    li { margin: 2px 0; }
    code {
      background: #f4f4f6;
      padding: 1px 5px;
      border-radius: 4px;
      font-size: 13px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    pre {
      background: #f4f4f6;
      padding: 8px 10px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 6px 0;
      code { background: none; padding: 0; }
    }
    blockquote {
      border-left: 3px solid #e5e7eb;
      padding-left: 10px;
      margin: 6px 0;
      color: #6b7280;
    }
    a {
      color: $el-color-primary;
      text-decoration: underline;
    }
    table {
      border-collapse: collapse;
      margin: 6px 0;
      font-size: 13px;
      th, td {
        border: 1px solid #e5e7eb;
        padding: 4px 8px;
      }
      th {
        background: #f9fafb;
        font-weight: 600;
      }
    }
    hr {
      border: none;
      border-top: 1px solid #e5e7eb;
      margin: 10px 0;
    }
    strong { font-weight: 600; }
    img {
      display: block;
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 6px 0;
      cursor: zoom-in;
    }
  }
}

@keyframes chat-typing {
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}

.chat-email-prompt {
  display: flex;
  gap: 6px;
  padding: 8px;
  background: #fff;
  border: 1px dashed $el-color-primary;
  border-radius: 10px;

  input {
    flex: 1;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 13px;
    outline: none;

    &:focus { border-color: $el-color-primary; }
  }

  button {
    padding: 6px 12px;
    background: $el-color-primary;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;

    &:disabled {
      background: #d4d4d4;
      cursor: not-allowed;
    }
  }
}
</style>
